/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { Component, OnInit, inject, signal, ViewEncapsulation } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DateInputComponent } from '@alfresco/aca-shared';
import { forkJoin } from 'rxjs';
import { ConnectorRegistry } from './connector-registry.service';
import { MigrationService } from './migration.service';
import { MigrationAuditEntry, MigrationJob } from './migration.models';

/**
 * History — every saved migration definition with filters, plus the audit
 * trail of what the center itself did (connection created, job saved, …).
 */
@Component({
  selector: 'app-mig-history',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, DateInputComponent, TranslatePipe],
  providers: [DatePipe],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './migration-history.component.html'
})
export class MigrationHistoryComponent implements OnInit {
  private readonly service = inject(MigrationService);
  private readonly registry = inject(ConnectorRegistry);
  private readonly translate = inject(TranslateService);
  private readonly datePipe = inject(DatePipe);

  readonly loading = signal(true);
  readonly jobs = signal<MigrationJob[]>([]);
  readonly audit = signal<MigrationAuditEntry[]>([]);

  search = '';
  statusFilter = '';
  connectorFilter = '';
  dateFrom = '';
  dateTo = '';

  readonly statuses = ['draft', 'ready', 'running', 'paused', 'completed', 'completed-warnings', 'failed', 'cancelled', 'interrupted'];

  ngOnInit(): void {
    forkJoin({ jobs: this.service.jobs(), audit: this.service.auditEntries() }).subscribe(({ jobs, audit }) => {
      this.jobs.set(jobs);
      this.audit.set(audit);
      this.loading.set(false);
    });
  }

  connectorName(id: string): string {
    const c = this.registry.get(id);
    return c ? this.translate.instant(c.titleKey) : (id ?? '—');
  }

  connectorsInUse(): string[] {
    return [
      ...new Set(
        this.jobs()
          .map((j) => j.sourceConnector)
          .filter(Boolean)
      )
    ];
  }

  filtered(): MigrationJob[] {
    const q = this.search.trim().toLowerCase();
    const from = this.dateFrom ? new Date(this.dateFrom + 'T00:00:00').getTime() : null;
    const to = this.dateTo ? new Date(this.dateTo + 'T23:59:59').getTime() : null;
    return this.jobs().filter((j) => {
      if (q && !`${j.name} ${j.id ?? ''}`.toLowerCase().includes(q)) {
        return false;
      }
      if (this.statusFilter && j.status !== this.statusFilter) {
        return false;
      }
      if (this.connectorFilter && j.sourceConnector !== this.connectorFilter) {
        return false;
      }
      if (from || to) {
        const t = new Date(j.createdAt || '').getTime();
        if (isNaN(t) || (from && t < from) || (to && t > to)) {
          return false;
        }
      }
      return true;
    });
  }

  statusKey(status: string): string {
    return `DMS.MIGRATION.ST_${(status || 'draft').toUpperCase().replace(/-/g, '_')}`;
  }

  dateLabel(iso?: string): string {
    if (!iso) {
      return '—';
    }
    const d = new Date(iso);
    return isNaN(d.getTime()) ? '—' : (this.datePipe.transform(d, 'dd/MM/yyyy HH:mm') ?? '—');
  }

  trackById(_i: number, job: MigrationJob): string {
    return job.id ?? job.name;
  }

  trackByAudit(_i: number, e: MigrationAuditEntry): string {
    return `${e.at}-${e.action}`;
  }
}
